import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Col, Row } from 'reactstrap';
import { sendServiceHistoryPhotoRecords } from '../AddVehicleActions';
import { resizeImage } from '../../common/utils/image/Image.utils';
import { UploadFileTypes } from '../../constants/UploadFileTypes';
import CommonDropzone from '../../common/components/CommonDropzone/CommonDropzone';
import CommonDeleteIconButton from '../../common/components/CommonDeleteIconButton';

const MAX_PHOTOS = 3;
const ACCEPT_TYPES = 'image/*';
const DROPZONE_TEXT = 'OR DRAG & DROP PHOTOS HERE';

function AddServiceHistoryPhoto({ className, maxPhotosLimit, disabled }) {
  const dispatch = useDispatch();
  const [isPhotoLoading, setPhotoLoading] = useState(false);

  const handleUploadPhoto = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const imageResized = await resizeImage(file);
      if (imageResized && imageResized?.type.includes(UploadFileTypes.IMAGE)) {
        setPhotoLoading(true);
        try {
          const action = await dispatch(sendServiceHistoryPhotoRecords(imageResized));

          if (!action?.payload?.photoRecord) {
            console.error('No photo record in action payload:', action);
          }
        } catch (error) {
          console.error('Error uploading photo:', error);
        } finally {
          setPhotoLoading(false);
        }
      }
    },
    [dispatch],
  );

  const handleDeletePhoto = useCallback(
    (photoId) => {
      dispatch({ type: 'REMOVE_PHOTO_IN_SERVICE_HISTORY', payload: { photoId } });
    },
    [dispatch],
  );

  const reduxPhotos = useSelector((state) => state.addVehicle.toJS().photoRecords || []);
  const photoLimit = maxPhotosLimit || MAX_PHOTOS;
  const slots = Math.max(0, photoLimit - reduxPhotos.length);
  const uploadDisabled = disabled || isPhotoLoading || reduxPhotos.length >= photoLimit;
  const currentPhotos = reduxPhotos;
  const maxPhotos = photoLimit;
  const remainingSlots = slots;
  const isUploadDisabled = uploadDisabled;
  return (
    <Row className={classNames('service-history-photos', className)}>
      {currentPhotos.map((photo, index) => (
        <Col md={4} key={photo.mediaMetadataId} className="mb-3">
          <div
            className="service-history__img-container"
            role="img"
            aria-label={`Service history photo ${index + 1} of ${currentPhotos.length}`}
          >
            <img
              src={photo.thumbnailUrl ? photo.thumbnailUrl : photo.fileUrl}
              alt={`Service history document ${index + 1}`}
              className="service-history__img"
              loading="lazy"
            />
            <div className="service-history__img-delete-icon-wrapper">
              <CommonDeleteIconButton
                handleClick={() => handleDeletePhoto(photo.mediaMetadataId)}
                aria-label={`Delete service history photo ${index + 1}`}
                disabled={isPhotoLoading}
              />
            </div>
          </div>
        </Col>
      ))}

      {Array.from({ length: remainingSlots }, (_, index) => (
        <Col md={4} key={`empty-slot-${index}`} className="mb-3">
          <CommonDropzone
            onDrop={handleUploadPhoto}
            accept={ACCEPT_TYPES}
            disabled={isUploadDisabled}
            dropzoneText={DROPZONE_TEXT}
            aria-label={`Upload service history photo ${currentPhotos.length + index + 1} of ${maxPhotos}`}
            role="button"
          />
        </Col>
      ))}
    </Row>
  );
}

AddServiceHistoryPhoto.propTypes = {
  form: PropTypes.shape({
    values: PropTypes.shape({
      serviceHistory: PropTypes.shape({
        photoRecords: PropTypes.arrayOf(
          PropTypes.shape({
            mediaMetadataId: PropTypes.string.isRequired,
            fileUrl: PropTypes.string.isRequired,
          }),
        ),
        photoRecordsToDelete: PropTypes.arrayOf(PropTypes.string),
      }),
    }).isRequired,
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  className: PropTypes.string,
  maxPhotosLimit: PropTypes.number,
  disabled: PropTypes.bool,
};

AddServiceHistoryPhoto.defaultProps = {
  className: '',
  maxPhotosLimit: MAX_PHOTOS,
  disabled: false,
};

export default AddServiceHistoryPhoto;
